import { useState, useEffect, useCallback, useMemo } from 'react';
import { digestService, DigestToolName, DigestResult } from '../services/digestService';

interface UseDigestOptions {
  tools: DigestToolName[];
  autoFetch?: boolean;
  refreshInterval?: number; // ms, 0 = no auto-refresh
}

interface UseDigestReturn {
  results: DigestResult[];
  byTool: Record<string, DigestResult>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useDigest({
  tools,
  autoFetch = true,
  refreshInterval = 0,
}: UseDigestOptions): UseDigestReturn {
  const [results, setResults] = useState<DigestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable key from tools array — avoids tools.join(',') in dep array
  const toolKey = useMemo(() => tools.join(','), [tools]);

  const refresh = useCallback(async () => {
    if (tools.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await digestService.fetch(tools);
      setResults(resp.results);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch digest');
    } finally {
      setLoading(false);
    }
  }, [toolKey]);

  useEffect(() => {
    if (autoFetch) refresh();
  }, [refresh, autoFetch]);

  useEffect(() => {
    if (!refreshInterval || refreshInterval <= 0) return;
    const id = setInterval(refresh, refreshInterval);
    return () => clearInterval(id);
  }, [refresh, refreshInterval]);

  // Memoized lookup — stable ref between renders when results don't change
  const byTool = useMemo(() => {
    const map: Record<string, DigestResult> = {};
    for (const r of results) map[r.tool_name] = r;
    return map;
  }, [results]);

  return { results, byTool, loading, error, refresh };
}
